import { logger } from '@beelday/common'
import { useEffect, useRef } from 'react'
import { useStageAudioOutput } from 'room/common/stage-audio-output'

const log = logger.create('media-streaming')

const SYSTEM_DEFAULT = ''

/**
 * Plays a recording's media element through the stage's audio output
 * (`room/common/stage-audio-output.tsx`), again whenever the output or the
 * element changes. A browser without `setSinkId` leaves the element on the
 * system default. When the browser refuses the output (it is gone, or not
 * allowed) the element goes back to the default and the stage is told -
 * nothing is thrown.
 */
export const useAudioSink = (element: HTMLMediaElement | undefined): void => {
	const { sinkId, onRefused } = useStageAudioOutput()
	const refused = useRef(onRefused)
	refused.current = onRefused
	// What the last request asked of an element, which starts on the default.
	const requested = useRef<{ element: HTMLMediaElement; sinkId: string }>()

	useEffect(() => {
		if (!element || typeof element.setSinkId !== 'function') {
			return
		}
		const before =
			requested.current?.element === element
				? requested.current.sinkId
				: SYSTEM_DEFAULT
		if (before === sinkId) {
			return
		}
		requested.current = { element, sinkId }
		let current = true
		element.setSinkId(sinkId).catch((error: unknown) => {
			if (!current) {
				return
			}
			log.warn(`The browser refused the audio output "${sinkId}"`, error)
			requested.current = { element, sinkId: SYSTEM_DEFAULT }
			element.setSinkId?.(SYSTEM_DEFAULT).catch(() => undefined)
			refused.current(sinkId)
		})
		return () => {
			current = false
		}
	}, [element, sinkId])
}
